// src/components/notify/console/NotifyForecastPanel.js
// FlowSense AI · Delivery Intelligence — pre-send forecast + live notification preview.
import React, { memo } from 'react';
import {
    Alert, AlertDescription, AlertIcon, Badge, Box, CircularProgress, CircularProgressLabel, Flex, HStack, Icon,
    SimpleGrid, Skeleton, Stack, Text, Tooltip, useColorModeValue, VStack,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdCheckCircle } from 'react-icons/md';
import { categoryMeta, priorityMeta } from '../notifyFormat';

const LEVEL_STATUS = { error: 'error', warning: 'warning', critical: 'error', info: 'info' };

function Metric({ label, value, tip }) {
    const muted = useColorModeValue('gray.500', 'gray.400');
    const body = (
        <Box>
            <Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="wide">{label}</Text>
            <Text fontSize="lg" fontWeight="800">{value}</Text>
        </Box>
    );
    return tip ? <Tooltip label={tip} hasArrow openDelay={300}>{body}</Tooltip> : body;
}

function NotifyForecastPanel({ preview, loading, error, title, body, category, priority, requiresAck }) {
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const phone = useColorModeValue('gray.900', 'black');
    const bubble = useColorModeValue('whiteAlpha.900', 'gray.700');
    const bubbleText = useColorModeValue('gray.800', 'white');
    const f = preview?.forecast;
    const cat = categoryMeta(preview?.category || category);
    const pr = priorityMeta(preview?.priority || priority);
    const shownTitle = preview?.title || title || 'Notification title';
    const shownBody = preview?.body || body || 'Your message appears here.';
    const rate = f?.predicted_read_rate ?? null;
    const rateColor = rate === null ? 'gray.300' : rate >= 70 ? 'green.400' : rate >= 45 ? 'orange.400' : 'red.400';

    return (
        <VStack align="stretch" spacing={4}>
            <Box bg={phone} borderRadius="2xl" p={4} pt={6} position="relative" aria-label="Notification preview">
                <Box position="absolute" top={2} left="50%" transform="translateX(-50%)" w="60px" h="5px" bg="whiteAlpha.400" borderRadius="full" />
                <Box bg={bubble} color={bubbleText} borderRadius="xl" p={3} boxShadow="md" borderLeftWidth="4px" borderLeftColor={pr.color === 'blue' ? `${cat.color}.400` : `${pr.color}.400`}>
                    <HStack spacing={2} mb={1}>
                        <Icon as={cat.icon} color={`${cat.color}.400`} boxSize={3.5} />
                        <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" opacity={0.7}>FlowSense · {cat.label}</Text>
                        <Text fontSize="2xs" opacity={0.6} ml="auto">now</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="800" noOfLines={2}>{shownTitle}</Text>
                    <Text fontSize="xs" noOfLines={4} whiteSpace="pre-line" opacity={0.85}>{shownBody}</Text>
                    {(preview?.requires_ack ?? requiresAck) && (
                        <HStack mt={2} spacing={1}><Icon as={MdCheckCircle} color="red.400" boxSize={3} /><Text fontSize="2xs" fontWeight="700" color="red.400">Acknowledge required</Text></HStack>
                    )}
                </Box>
            </Box>

            <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
                <HStack spacing={2} mb={3}>
                    <Icon as={MdAutoAwesome} color="purple.500" />
                    <Text fontSize="sm" fontWeight="800">FlowSense AI forecast</Text>
                    {f && <Badge colorScheme={f.confidence === 'high' ? 'green' : f.confidence === 'medium' ? 'blue' : 'gray'} variant="subtle" ml="auto">{f.confidence} confidence</Badge>}
                </HStack>
                {error ? (
                    <Alert status="warning" borderRadius="lg" fontSize="sm"><AlertIcon /><AlertDescription>{error.message}</AlertDescription></Alert>
                ) : loading && !f ? (
                    <Stack spacing={2}><Skeleton h="70px" borderRadius="lg" /><Skeleton h="40px" borderRadius="lg" /></Stack>
                ) : !f ? (
                    <Text fontSize="sm" color={muted}>Pick an audience and write a title to see who it will reach and how likely they are to read it.</Text>
                ) : (
                    <VStack align="stretch" spacing={3} opacity={loading ? 0.6 : 1} transition="opacity .2s">
                        <Flex align="center" gap={4}>
                            <CircularProgress value={rate || 0} color={rateColor} size="84px" thickness="10px" aria-label={`Predicted read rate ${rate}%`}>
                                <CircularProgressLabel fontSize="md" fontWeight="800">{rate === null ? '—' : `${Math.round(rate)}%`}</CircularProgressLabel>
                            </CircularProgress>
                            <SimpleGrid columns={2} spacing={3} flex="1">
                                <Metric label="Recipients" value={f.recipients} />
                                <Metric label="Likely readers" value={f.predicted_readers} tip="Expected number of people who will read it" />
                                <Metric label="Reach now" value={f.reach_now} tip={`Online now, or reachable by browser push / mobile push (${f.with_mobile_push ?? 0} with the tech app)`} />{/* --- UPDATE (FCM channel) */}
                                <Metric label="Silent" value={f.silent} tip="Quiet hours, muted, or push off for this topic — still in their inbox" />
                            </SimpleGrid>
                        </Flex>
                        {f.best_hour && <Text fontSize="xs" color={muted}>Best send window: <b>{f.best_hour.label}</b> ({f.best_hour.read_within_1h_pct}% read within an hour)</Text>}
                        {f.hints.map((h, i) => (
                            <Alert key={i} status={LEVEL_STATUS[h.level] || 'info'} borderRadius="lg" py={2} fontSize="xs" variant="left-accent">
                                <AlertIcon boxSize={3.5} /><AlertDescription>{h.text}</AlertDescription>
                            </Alert>
                        ))}
                        {f.fatigued?.length > 0 && (
                            <Box>
                                <Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase" mb={1}>Fatigue risk</Text>
                                {f.fatigued.slice(0, 4).map(u => <Text key={u.id} fontSize="xs">{u.name} — {u.last_24h} in 24 h ({u.unread} unread)</Text>)}
                            </Box>
                        )}
                        <Text fontSize="2xs" color={muted}>{f.model} · learned from your FlowSense delivery history</Text>
                    </VStack>
                )}
            </Box>
        </VStack>
    );
}

export default memo(NotifyForecastPanel);
